import { useMemo } from 'react';

import { useCompanyData } from '~/data/company';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { CompensationEqualityReportData } from '~/data/reports';
import {
  CompensationEqualityReportEntry,
  convertCompensationEqualityReportDataToEntries,
} from '~/scenes/PortalModules/Analytics/Rewarding/CompensationEqualityReport/data';

import {
  ColumnFilterProps,
  useFilterCompensationEqualityRowsBySelector,
} from './useFilterCompensationEqualityRowsBySelector';

type UseCompensationEqualityReportOutput = {
  rows: CompensationEqualityReportEntry[];
  positionFilter: ColumnFilterProps;
  currencyFilter: ColumnFilterProps;
  filteringStageRowCounts: {
    allRows: number;
    validRows: number;
    positionRows: number;
    currencyRows: number;
  };
};

type PropsType = {
  data: CompensationEqualityReportData[];
};

export const useCompensationEqualityReport = (
  props: PropsType,
): UseCompensationEqualityReportOutput => {
  const { data: allRows } = props;

  const { companyData } = useCompanyData();

  const { getEnums } = useAllEnums();
  const { employeePositionEnums, currencyEnums } = useMemo(() => {
    return {
      employeePositionEnums: getEnums(EnumListKey.EMPLOYEE_POSITION),
      currencyEnums: getEnums(EnumListKey.CURRENCY),
    };
  }, [getEnums]);

  const { validRows } = useMemo(() => {
    const validRows = allRows.filter((row) => {
      const { fEmpSalary, fEmpGender } = row;

      // Only show Monthly salaries of people marked as either Male or Female
      return (
        !!fEmpSalary &&
        fEmpSalary.fSalaryFrequency === 'MONTHLY' &&
        ['M', 'F'].includes(fEmpGender)
      );
    });

    return {
      validRows,
    };
  }, [allRows]);

  const { rows: positionFilteredRows, ...positionFilter } =
    useFilterCompensationEqualityRowsBySelector({
      defaultValue: employeePositionEnums[0].code,
      rows: validRows,
      getFilterRowValue: (row) => row.fEmpPosition ?? '',
      filterEnums: employeePositionEnums,
    });

  const { rows: currencyFilteredRows, ...currencyFilter } =
    useFilterCompensationEqualityRowsBySelector({
      defaultValue: companyData.currencies.fComDefaultCurrency,
      rows: positionFilteredRows,
      getFilterRowValue: (row) => row.fEmpCurrency ?? '',
      filterEnums: currencyEnums,
    });

  const filteringStageRowCounts: UseCompensationEqualityReportOutput['filteringStageRowCounts'] =
    {
      allRows: allRows.length,
      validRows: validRows.length,
      positionRows: positionFilteredRows.length,
      currencyRows: currencyFilteredRows.length,
    };

  const aggregatedRows =
    convertCompensationEqualityReportDataToEntries(currencyFilteredRows);

  return {
    rows: aggregatedRows,
    positionFilter,
    currencyFilter,
    filteringStageRowCounts,
  };
};
